import { styled, Theme } from '@nexelem/deprecated-theme';
import {
  GridCellCheckboxRenderer,
  gridFilteredDescendantCountLookupSelector,
  GridRenderCellParams,
  GridValidRowModel,
  selectedIdsLookupSelector,
  useGridApiContext,
  useGridSelector,
  GridBasicGroupNode,
} from '@mui/x-data-grid-pro';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { ProLightAngleDown } from '@nexelem/icons/ProLightAngleDown';
import { ProLightAngleUp } from '@nexelem/icons/ProLightAngleUp';
import { ProLightCircleExclamation } from '@nexelem/icons/ProLightCircleExclamation';
import { Badge, IconButtonProps, IconButton, Status } from '@nexelem/components';
import { formatters, LongStringSpan } from '@nexelem/utils';

export const StyledHelperIcon = styled(ProLightCircleExclamation)(({ theme }) => {
  const typedTheme = theme as Theme;
  const { header } = typedTheme.client.table;
  const { getColor } = typedTheme.palette;

  return {
    marginRight: typedTheme.spacing(2.5),
    color: getColor(header.alertIcon),
    height: typedTheme.spacing(4),
    width: typedTheme.spacing(4),
  };
});

const Product = styled('div')(({ theme }) => {
  return {
    height: '100%',
    overflow: 'visible',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    display: 'flex',
    alignItems: 'center',
    span: {
      marginRight: theme.spacing(1.75),
    },
  };
});

const StyledIconButton = styled(IconButton)(({ theme }) => {
  return {
    marginLeft: theme.spacing(-2.5),
  };
});

export const CellNumberLowerThanValue = (value: number) => (params: GridRenderCellParams<any, number>) => (
  <>
    {params.value && params.value <= value ? <StyledHelperIcon /> : null}
    {formatters.number.format(params)}
  </>
);

export const CellStatus = (params: GridRenderCellParams) =>
  params.value ? <Status color={params.value.isDisabled ? 'grey' : 'green'} text={params.value.status} /> : null;

export const CellProduct = <T extends GridValidRowModel>(params: GridRenderCellParams<T, string | string[]>) => {
  const { value, rowNode } = params;
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);
  const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;
  const label = Array.isArray(value) ? value[value.length - 1] : value;

  return (
    <Product>
      <LongStringSpan>{label}</LongStringSpan>
      {filteredDescendantCount > 0 ? (
        <Badge StartIcon={<CUSTOM_ICONS.Merge />} variant="highlighted">
          {filteredDescendantCount}
        </Badge>
      ) : null}
    </Product>
  );
};

export const CellGrouping = (props: GridRenderCellParams) => {
  const { id, field, rowNode } = props;
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);
  const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleClick: IconButtonProps['onClick'] = (event) => {
    apiRef.current.setRowChildrenExpansion(id, !(rowNode as GridBasicGroupNode).childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  if (filteredDescendantCount === 0) {
    return null;
  }

  return (
    <div className="flex items-center justify-center">
      <StyledIconButton
        Icon={!(rowNode as GridBasicGroupNode).childrenExpanded ? ProLightAngleDown : ProLightAngleUp}
        onClick={handleClick}
      />
    </div>
  );
};

export const CellCheckbox = (params: GridRenderCellParams) => {
  //based on https://github.com/mui/mui-x/issues/4248
  const apiRef = useGridApiContext();
  const { rowNode } = params;

  if (rowNode.type !== 'group') {
    return <GridCellCheckboxRenderer {...params} />;
  }

  const selectionLookup = selectedIdsLookupSelector(apiRef.current.state, apiRef.current.instanceId);

  const children = apiRef.current.getRowGroupChildren({
    groupId: rowNode.id,
    applyFiltering: true,
    applySorting: true,
  });

  const indeterminate =
    children?.some((child) => selectionLookup[child] === undefined) &&
    children?.some((child) => selectionLookup[child] !== undefined);

  const checked = children?.every((child) => selectionLookup[child] !== undefined);

  const extraData: GridRenderCellParams & {
    indeterminate?: boolean;
    checked?: boolean;
    disabled?: boolean;
    onClick?: (e: MouseEvent) => void;
  } = {
    ...params,
    disabled: false,
    onClick: (e) => {
      if (rowNode.type === 'group') {
        if (children) {
          apiRef.current.selectRows(children, indeterminate || !checked);
        }
        e.preventDefault();
      }
    },
    indeterminate,
    checked,
  };

  return <GridCellCheckboxRenderer {...extraData} />;
};
